/**
 * LES BOUTONS ENTRE LES PAGES D'UNE LISTE (AB#880).
 *
 * « Précédent » · 1 2 3 … · « Page 2 sur 5 » · « Suivant » (les numéros :
 * AB#888), et sous eux l'étendue
 * (« 21 à 40 sur 87 chapitres »). De vrais `<button>` : atteints à la
 * tabulation, actionnés à Entrée ou Espace, DÉSACTIVÉS aux bornes. La position
 * est annoncée (`aria-live`) quand elle change.
 *
 * Une liste qui tient sur une page n'a pas de boutons : seulement son étendue.
 *
 * La page vit dans l'adresse : `usePageDansAdresse()`. Changer de page AJOUTE
 * une entrée d'historique — le retour arrière remonte les pages, et revenir
 * d'un chapitre ramène à celle qu'on quittait.
 */
import { useCallback, useRef, type ReactNode, type RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  PARAM_PAGE,
  adresseDePage,
  direEtendue,
  lirePage,
  numerosDePages,
  paginer,
  type PageDeListe,
} from '../lib/pagination';

export function Pagination({
  page,
  singulier,
  pluriel,
  onPage,
  testId = 'pagination',
  enTete = false,
  masquerSiUnePage = false,
  classe,
}: {
  readonly page: Pick<PageDeListe<unknown>, 'page' | 'nbPages' | 'premier' | 'dernier' | 'total'>;
  /** Le nom de ce qu'on compte : « chapitre ». */
  readonly singulier: string;
  readonly pluriel?: string;
  readonly onPage: (page: number) => void;
  readonly testId?: string;
  /**
   * La copie AU-DESSUS de la liste (AB#888 : sous une liste de 20, les boutons
   * étaient à 2 000 px, et rien ne disait qu'il y avait d'autres pages). Elle
   * n'annonce rien (la copie du bas le fait), et disparaît sur une seule page.
   */
  readonly enTete?: boolean;
  /** Une seule page : rien du tout, pas même l'étendue (une section de fiche). */
  readonly masquerSiUnePage?: boolean;
  /** Une classe de plus (`pagination--file` dans la file de relecture). */
  readonly classe?: string;
}) {
  const etendue = direEtendue(page, singulier, pluriel);
  if (page.nbPages <= 1 && (enTete || masquerSiUnePage)) return null;
  if (page.nbPages <= 1) {
    return (
      <p className="note-line pagination__etendue" data-testid={`${testId}-etendue`}>
        {etendue}
      </p>
    );
  }
  const premiere = page.page <= 1;
  const derniere = page.page >= page.nbPages;
  return (
    <nav
      className={['pagination', enTete ? 'pagination--tete' : null, classe ?? null]
        .filter((c) => c !== null)
        .join(' ')}
      aria-label={`Pages des ${pluriel ?? `${singulier}s`}${enTete ? ' (haut de la liste)' : ''}`}
      data-testid={testId}
    >
      <div className="pagination__boutons">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={premiere}
          onClick={() => onPage(page.page - 1)}
          data-testid={`${testId}-precedent`}
        >
          <span aria-hidden="true">←</span> Précédent
        </button>
        <span className="pagination__numeros" data-testid={`${testId}-numeros`}>
          {numerosDePages(page.page, page.nbPages).map((n, i) =>
            n === null ? (
              <span key={`saut-${i}`} className="pagination__saut" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                className={
                  n === page.page
                    ? 'pagination__numero pagination__numero--courant'
                    : 'pagination__numero'
                }
                aria-label={`Page ${n}`}
                aria-current={n === page.page ? 'page' : undefined}
                onClick={() => (n === page.page ? undefined : onPage(n))}
                data-testid={`${testId}-numero-${n}`}
              >
                {n}
              </button>
            ),
          )}
        </span>
        <span
          className="pagination__position"
          aria-live={enTete ? undefined : 'polite'}
          data-testid={`${testId}-position`}
        >
          Page {page.page} sur {page.nbPages}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={derniere}
          onClick={() => onPage(page.page + 1)}
          data-testid={`${testId}-suivant`}
        >
          Suivant <span aria-hidden="true">→</span>
        </button>
      </div>
      <p className="note-line pagination__etendue" data-testid={`${testId}-etendue`}>
        {etendue}
      </p>
    </nav>
  );
}

/**
 * La page lue dans l'adresse, et le geste pour en changer. `ancre` se pose sur
 * le haut de la liste : changer de page y ramène la vue, sinon on arrive en
 * bas d'une page neuve, là où étaient les boutons.
 */
export function usePageDansAdresse(param: string = PARAM_PAGE): {
  readonly page: number;
  readonly allerA: (page: number) => void;
  readonly ancre: RefObject<HTMLDivElement | null>;
} {
  const [params, setParams] = useSearchParams();
  const ancre = useRef<HTMLDivElement | null>(null);
  const allerA = useCallback(
    (page: number) => {
      setParams((p) => adresseDePage(p, page, param));
      ancre.current?.scrollIntoView?.({ block: 'start' });
    },
    [setParams, param],
  );
  return { page: lirePage(params, param), allerA, ancre };
}

/**
 * UNE LISTE PAGINÉE DANS UNE SECTION (AB#888) — la fiche d'un chapitre porte
 * plusieurs listes (fiches, capsules, erreurs, attentes…) : chacune a SON
 * paramètre d'adresse (`param`), pour qu'aucune ne feuillette l'autre. Une
 * liste qui tient sur une page ne montre aucun bouton, ni aucune étendue.
 */
export function ListePaginee<T>({
  elements,
  param,
  singulier,
  pluriel,
  testId,
  children,
}: {
  readonly elements: readonly T[];
  readonly param: string;
  readonly singulier: string;
  readonly pluriel?: string;
  readonly testId: string;
  readonly children: (page: readonly T[]) => ReactNode;
}) {
  const { page, allerA, ancre } = usePageDansAdresse(param);
  const decoupe = paginer(elements, page);
  return (
    <div ref={ancre} data-testid={testId}>
      {children(decoupe.elements)}
      <Pagination
        page={decoupe}
        singulier={singulier}
        pluriel={pluriel}
        onPage={allerA}
        testId={`${testId}-pages`}
        masquerSiUnePage
      />
    </div>
  );
}
